'use client'

import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { z } from 'zod/v4'

import { MAX_SCHOOL_REMOVAL_RESENDS } from '@package/drizzle/schema'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  Textarea,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { useSharedTRPC } from '../../../context'
import { getPendingRemovalRequest } from '../types'
import { useStudentMutations } from '../use-student-mutations'

/** Mirrors `MAX_REMOVAL_MESSAGE_LENGTH` on the router, so the counter and the server agree. */
const MAX_MESSAGE_LENGTH = 1000

/**
 * Prefill, not a default the server knows about. The parent may clear it entirely — the request
 * reads correctly with no note, and the router stores an empty string as `null`.
 */
const MESSAGE_PREFILL = 'Mohon keluarkan anak saya dari akun sekolah agar saya dapat menghapus akunnya karena…'

const FormSchema = z.object({
  message: z
    .string()
    .trim()
    .max(MAX_MESSAGE_LENGTH, { message: `Maksimal ${MAX_MESSAGE_LENGTH} karakter.` }),
})

interface DeleteChildAccountDialogProps {
  student: Student
  onClose: () => void
}

/**
 * Deletes a child's account from the family side — or explains why it cannot be, yet.
 *
 * A child enrolled at a school cannot be deleted here: the account is shared with that school, and
 * the server refuses (`PRECONDITION_FAILED`). Rather than surface that as a failed action, this
 * dialog reads `schoolConnection` up front and opens straight into the request step, so the plain
 * confirm is never offered for a deletion that would be rejected. The server guard remains the
 * backstop for a stale row — a connect-accept can land between page load and click.
 *
 * The request is a nudge with a budget: one live request per child per school, three resends. There
 * is no accept or reject on the school's side; they answer by using "Keluarkan dari sekolah", and
 * the parent then comes back here and deletes.
 */
export function DeleteChildAccountDialog({ student, onClose }: DeleteChildAccountDialogProps) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const school = student.schoolConnection
  const pendingRemoval = getPendingRemovalRequest(student)
  const gradeLabel = schoolGradeLabel(student.schoolGrade)

  const deleteChildren = useMutation(trpc.myOrganization.deleteChildren.mutationOptions())
  const requestRemoval = useMutation(trpc.homeConnect.requestSchoolRemoval.mutationOptions())
  const resendRemoval = useMutation(trpc.homeConnect.resendSchoolRemoval.mutationOptions())

  const isSending = requestRemoval.isPending || resendRemoval.isPending
  const resendsLeft = pendingRemoval
    ? MAX_SCHOOL_REMOVAL_RESENDS - pendingRemoval.resendCount
    : MAX_SCHOOL_REMOVAL_RESENDS
  const atCap = pendingRemoval !== null && resendsLeft <= 0

  const form = useForm({
    defaultValues: { message: pendingRemoval ? '' : MESSAGE_PREFILL } as z.input<typeof FormSchema>,
    validators: { onSubmit: FormSchema },
    onSubmit: async ({ value }) => {
      try {
        if (pendingRemoval) {
          const result = await resendRemoval.mutateAsync({ requestId: pendingRemoval.requestId })
          toast.success(`Permintaan dikirim ulang ke ${result.schoolName}.`)
        } else {
          const result = await requestRemoval.mutateAsync({
            childId: student.id,
            message: value.message.trim() ? value.message : undefined,
          })
          toast.success(`Permintaan terkirim ke ${result.schoolName}.`)
        }
        // The roster row carries the request state, so it has to be refetched for the dialog to
        // reopen in the "already sent" shape.
        await invalidate()
        onClose()
      } catch (error) {
        // A CONFLICT here usually means the roster row was stale (the school already resolved the
        // request) — refetch so the dialog reopens in the right shape instead of the same dead button.
        await invalidate()
        toast.error(error instanceof Error ? error.message : 'Gagal mengirim permintaan')
      }
    },
  })

  const handleDelete = async () => {
    try {
      await deleteChildren.mutateAsync({ userIds: [student.id] })
      // `invalidate` covers the roster and the seat queries. Do NOT add a
      // `getActiveMembershipPremiums` invalidation here — that procedure no longer exists.
      await invalidate()
      toast.success(`Akun ${student.name} berhasil dihapus.`)
      onClose()
    } catch (error) {
      // Same reasoning as the resend/request catch above: refetch so a stale roster row does not
      // reopen this dialog on a button that can only fail again.
      await invalidate()
      toast.error(error instanceof Error ? error.message : 'Gagal menghapus akun')
    }
  }

  const childRow = (
    <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-2'>
      <Avatar profile='user'>
        {student.image && <AvatarImage src={student.image} alt={student.name} />}
        <AvatarFallback>{getInitials(student.name)}</AvatarFallback>
      </Avatar>
      <span className='srd:flex srd:flex-col'>
        <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
          {student.name}
        </span>
        {gradeLabel && (
          <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-70')}>{gradeLabel}</span>
        )}
      </span>
    </div>
  )

  if (school.status === 'none') {
    return (
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
      >
        <DialogContent className='srd:w-full srd:max-w-md'>
          <DialogHeader>
            <DialogTitle>Yakin hapus akun anak?</DialogTitle>
            <DialogDescription>
              Akun ini akan <strong>dihapus permanen.</strong>
            </DialogDescription>
          </DialogHeader>

          {childRow}

          <DialogFooter>
            <Button variant='outline' onClick={onClose} disabled={deleteChildren.isPending}>
              Batal
            </Button>
            <Button
              variant='destructive'
              onClick={() => void handleDelete()}
              loading={deleteChildren.isPending}
              disabled={deleteChildren.isPending}
            >
              Ya, hapus saja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className='srd:flex srd:max-h-[95dvh] srd:w-full srd:max-w-md srd:flex-col srd:overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Perlu keluarkan anak ini dari sekolahnya dulu sebelum menghapus akun</DialogTitle>
          <DialogDescription>
            {student.name} masih terdaftar di sekolah ini. Keluarkan dari sekolah dulu sebelum menghapus akun{' '}
            {student.name}.
          </DialogDescription>
        </DialogHeader>

        {childRow}

        <div className='srd:flex srd:flex-col srd:gap-y-2'>
          <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-70')}>Sekolah</span>
          <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-2'>
            <Avatar profile='organization'>
              {school.schoolLogo && <AvatarImage src={school.schoolLogo} alt={school.schoolName} />}
              <AvatarFallback>{getInitials(school.schoolName)}</AvatarFallback>
            </Avatar>
            <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
              {school.schoolName}
            </span>
          </div>
        </div>

        <form
          className='srd:flex srd:flex-col srd:gap-4'
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          {!pendingRemoval && (
            <FieldSet disabled={isSending}>
              <form.Field name='message'>
                {(field) => (
                  <Field>
                    <FieldLabel>Pesan tambahan</FieldLabel>
                    <Textarea
                      rows={4}
                      maxLength={MAX_MESSAGE_LENGTH}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )}
              </form.Field>
            </FieldSet>
          )}

          {atCap && (
            <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-70')}>
              Permintaan sudah terkirim. Tunggu sekolah memproses.
            </span>
          )}

          <DialogFooter className='srd:flex-col srd:items-stretch srd:gap-y-2'>
            <Button type='submit' loading={isSending} disabled={isSending || atCap}>
              <Send className='srd:mr-2 srd:h-4 srd:w-4' />
              {pendingRemoval ? `Kirim ulang permintaan (${resendsLeft} tersisa)` : 'Kirim permintaan ke sekolah'}
            </Button>
            <Button type='button' variant='outline' onClick={onClose} disabled={isSending}>
              Kembali
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
